import { FC, PropsWithChildren, ReactNode, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import { cn } from '@/utils/classNames.ts';
import Portal from '@/components/shared/Portal/Portal.tsx';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button } from 'react-daisyui';
import { ModalProvider, useModalContext } from '@/components/modals/Modal/ModalContext.tsx';

export type ModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  closeable?: boolean;
  wrapperClassName?: string;
};

const ModalWrapper: FC<PropsWithChildren<PropsWithClassName<ModalProps>>> = ({
  children,
  className,
  wrapperClassName,
  ...rest
}) => {
  return (
    <ModalProvider {...rest}>
      <Modal wrapperClassName={wrapperClassName} className={className}>
        {children}
      </Modal>
    </ModalProvider>
  );
};

const Modal: FC<PropsWithChildren<PropsWithClassName<{ wrapperClassName?: string }>>> = ({
  children,
  className,
  wrapperClassName,
}) => {
  const { modalRef, modalId, titleId, handleClose, showCloseButton, closeable, navId } = useModalContext();
  const [visible, setVisible] = useState(false);

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setVisible(true);
      });
    });
  }, []);

  const portalTarget = document.querySelector('#root [data-theme]') ?? document.body;

  return createPortal(
    <div ref={modalRef} id={modalId} className={cn('modal', { 'modal-open': visible })}>
      <div className={cn('modal-box relative pb-0 pt-0 bg-base-100', wrapperClassName)}>
        <div className="bg-base-100 sticky right-4 top-0 pt-6 pb-4 flex justify-between items-start gap-4 z-10">
          <div className="w-full" id={titleId}></div>
          {showCloseButton && (
            <Button onClick={handleClose} color="ghost" shape="circle" size="xs">
              <XMarkIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
        <div className={cn('max-w-full', className)}>{children}</div>
        <div
          className="flex gap-4 pt-4 pb-6 justify-between flex-wrap sticky bottom-0 bg-base-100 empty:pt-0"
          id={navId}
        ></div>
      </div>

      <div className="modal-backdrop">
        <button onClick={closeable ? handleClose : undefined}>close</button>
      </div>
    </div>,
    portalTarget
  );
};

type ModalNavProps = {
  children: (props: ReturnType<typeof useModalContext>) => ReactNode;
};
const ModalNav: FC<ModalNavProps> = ({ children }) => {
  const { navId, ...rest } = useModalContext();
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId={navId}>
      {children({ navId, ...rest })}
    </Portal>
  );
};

type ModalTitleProps = {
  render?: (props: ReturnType<typeof useModalContext>) => ReactNode;
};
const ModalTitle: FC<PropsWithChildren<ModalTitleProps>> = ({ children, render }) => {
  const { titleId, ...rest } = useModalContext();
  const [watch, setWatch] = useState(false);

  useLayoutEffect(() => {
    setWatch((prev) => !prev);
  }, []);

  return (
    <Portal watch={watch} elementId={titleId}>
      {children}
      {render ? render({ titleId, ...rest }) : null}
    </Portal>
  );
};

export default {
  Wrapper: ModalWrapper,
  Nav: ModalNav,
  Title: ModalTitle,
};
